import { Col, Divider, Modal, Row, Typography } from "antd"
import { observer } from "mobx-react"
import { useTranslation } from "react-i18next"
import MainForm, { MainFormRef } from "../../../components/Form/MainForm"
import { createRef, useEffect, useState } from "react"
import { SellerOffice } from "../../../../domain/entities/SellerOffice"
import { FormField } from "../../../components/Form/FormField"
import { InputType } from "../../../components/TextInput/const/InputType.enum"
import config from "../../../../config/config"
import Switch from "../../../components/Switch/Switch"

export interface ServiceSessionModalProps {
	onConfirm?: (modalData: any) => void
	onCancel?: () => void
	visible: boolean
	data?: Partial<SellerOffice>
}

const SellerOfficeModal = ({ visible, onConfirm, data, onCancel }: ServiceSessionModalProps) => {
	const { t } = useTranslation("sellers", { keyPrefix: "form.offices" })
	const { t: tFields } = useTranslation("sellers", { keyPrefix: "form.fields" })
	const { t: tCountries } = useTranslation("countries")
	const formRef = createRef<MainFormRef>()
	const [modalData, setModalData] = useState({
		id: data?.id,
		name: data?.name,
		address: data?.address,
		city: data?.city,
		fiscalName: data?.fiscalName,
		fiscalCountry: data?.fiscalCountry,
		fiscalAddress: data?.fiscalAddress,
		paymentDays: data?.paymentDays,
		nif: data?.nif,
		iban: data?.iban,
		feePercent: data?.feePercent
	})
	useEffect(() => {
		if (!visible) {
			setModalData({
				id: undefined,
				name: undefined,
				address: undefined,
				city: undefined,
				fiscalName: undefined,
				fiscalCountry: undefined,
				fiscalAddress: undefined,
				paymentDays: undefined,
				nif: undefined,
				iban: undefined,
				feePercent: undefined
			})
		} else {
			setModalData({
				id: data?.id,
				name: data?.name,
				address: data?.address,
				city: data?.city,
				fiscalName: data?.fiscalName,
				fiscalCountry: data?.fiscalCountry,
				fiscalAddress: data?.fiscalAddress,
				paymentDays: data?.paymentDays,
				nif: data?.nif,
				iban: data?.iban,
				feePercent: data?.feePercent
			})
		}
	}, [visible])
	return (
		<Modal
			title={
				<div style={{ position: "relative", bottom: 24 }}>
					<Typography.Title level={5}>{t("new")}</Typography.Title>
					<Divider style={{ marginBottom: 0 }} />
				</div>
			}
			centered
			style={{ overflowY: "auto" }}
			open={visible}
			onOk={async () => {
				try {
					await formRef.current?.validateFields()
					onConfirm && onConfirm(modalData)
				} catch {
					// Invalid fields
				}
			}}
			onCancel={() => onCancel && onCancel()}
			width={600}
			okText={t("confirm")}
			cancelText={t("cancel")}
		>
			<MainForm
				className="pl-4 pb-3"
				initialValues={modalData}
				hideFormButtons
				onChangeFormValues={formData => {
					const parsed = {
						...formData,
						paymentDays: formData.paymentDays ? Number(formData.paymentDays) : null,
						feePercent: formData.feePercent ? Number(formData.feePercent) : null
					}
					if (JSON.stringify(modalData) !== JSON.stringify(parsed)) {
						setModalData({
							...modalData,
							...parsed
						})
					}
				}}
				ref={formRef}
			>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Input key={"name"} label={t("name")} name={"name"} mandatory />
					</Col>
				</Row>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Input key={"city"} label={t("city")} name={"city"} />
					</Col>
				</Row>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Input key={"address"} label={t("address")} name={"address"} />
					</Col>
				</Row>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Input key={"fiscalName"} label={tFields("fiscalName")} name={"fiscalName"} />
					</Col>
				</Row>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Select
							key={"fiscalCountry"}
							label={tFields("fiscalCountry")}
							name={"fiscalCountry"}
							options={config.tax.fiscalCountries.map(country => ({
								value: country,
								label: tCountries(country),
								key: country
							}))}
						/>
					</Col>
				</Row>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Input
							key={"fiscalAddress"}
							label={tFields("fiscalAddress")}
							name={"fiscalAddress"}
						/>
					</Col>
				</Row>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Input
							key={"paymentDays"}
							label={tFields("paymentDays")}
							name={"paymentDays"}
							inputType={InputType.NUMBER}
						/>
					</Col>
				</Row>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Input key={"nif"} label={tFields("nif")} name={"nif"} />
					</Col>
				</Row>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Input key={"iban"} label={tFields("iban")} name={"iban"} />
					</Col>
				</Row>
				<Row gutter={[24, 0]}>
					<Col xl={24} md={24} xs={24}>
						<FormField.Input
							key={"feePercent"}
							label={tFields("feePercentage")}
							name={"feePercent"}
							inputType={InputType.NUMBER}
							preFix={"%"}
						/>
					</Col>
				</Row>
			</MainForm>
		</Modal>
	)
}

export default observer(SellerOfficeModal)
